import {
    auth,
    balanceHistory,
    bootstrap,
    claim,
    contact,
    join,
    loyalty,
    profile,
    referrals,
    register,
} from '@routes/api/v1/mini-app';
import {
    store as storeAvatar,
    destroy as destroyAvatar,
} from '@routes/api/v1/mini-app/avatar';
import {
    availability as topUpOptions,
    show as topUpShow,
    start as topUpStart,
} from '@routes/api/v1/mini-app/online-topup';
import { discard, scan } from '@routes/api/v1/mini-app/qr';
import { confirm, decline } from '@routes/api/v1/mini-app/session';
import type {
    Account,
    AuthResult,
    BalanceHistory,
    ClubLoyalty,
    OnlinePaymentView,
    ReferralOverview,
    ScanResult,
    TopUpOptions,
} from './types';

type Route = { url: string; method: string };
type Envelope<T> = { data: T };

export class ApiError extends Error {
    constructor(
        message: string,
        readonly status: number,
        readonly errors: Record<string, string[]> = {},
        /** A machine-readable reason, e.g. `init_data_expired` (2026-10-07). */
        readonly code: string | null = null,
    ) {
        super(message);
    }
}

export class MiniAppApi {
    private readonly base = (
        import.meta.env.VITE_MINI_APP_API_URL ?? 'https://testing.highlink.ai'
    ).replace(/\/$/, '');
    private sessionToken: string | null = null;
    private accessToken: string | null = null;

    /**
     * Signs in again when the access token has run out (an hour), returning whether it worked
     * (2026-10-07). Telegram's init data is accepted for a day, so a Mini App left open past the
     * hour re-authenticates with it silently instead of failing every request.
     */
    onExpired: (() => Promise<boolean>) | null = null;
    private renewing: Promise<boolean> | null = null;

    setCredentials(sessionToken: string, accessToken: string | null): void {
        this.sessionToken = sessionToken;
        this.accessToken = accessToken;
    }

    /** One sign-in for every request that hit the expiry at once. */
    private renew(): Promise<boolean> {
        if (!this.onExpired) {
            return Promise.resolve(false);
        }

        this.renewing ??= this.onExpired()
            .catch(() => false)
            .finally(() => {
                this.renewing = null;
            });

        return this.renewing;
    }

    authenticate(
        initData: string,
        startParam?: string,
        renew = false,
    ): Promise<AuthResult> {
        return this.json<AuthResult>(auth(), {
            init_data: initData,
            start_param: startParam,
            // The same launch signing in again: its link is not applied a second time.
            ...(renew ? { renew: true } : {}),
        });
    }

    contactStatus(): Promise<{ received: boolean; phone: string | null }> {
        return this.json(contact());
    }

    claim(): Promise<{ access_token: string; account: Account }> {
        return this.json(claim(), {});
    }

    register(
        data: Record<string, unknown>,
    ): Promise<{ access_token: string; account: Account }> {
        return this.json(register(), data);
    }

    bootstrap(): Promise<Account> {
        return this.json(bootstrap());
    }

    referrals(networkId: number): Promise<ReferralOverview> {
        return this.json(referrals({ query: { network_id: networkId } }));
    }

    /** «Історія операцій» of one network's balance (2026-10-28), a page at a time. */
    balanceHistory(
        networkId: number,
        cursor: string | null = null,
    ): Promise<BalanceHistory> {
        return this.json(
            balanceHistory({
                query: {
                    network_id: networkId,
                    ...(cursor ? { cursor } : {}),
                },
            }),
        );
    }

    /** «Квести й досягнення» of one joined club (2026-10-15). */
    loyalty(clubId: number): Promise<ClubLoyalty> {
        return this.json(loyalty({ query: { club_id: clubId } }));
    }

    join(): Promise<Account> {
        return this.json(join(), {});
    }

    scan(payload: string): Promise<ScanResult> {
        return this.json(scan(), { payload });
    }

    discardTarget(): Promise<{ discarded: boolean }> {
        return this.json(discard());
    }

    updateProfile(data: Record<string, unknown>): Promise<Account> {
        return this.json(profile(), data);
    }

    confirmSession(): Promise<{ status: string }> {
        return this.json(confirm(), {});
    }

    declineSession(): Promise<{ status: string }> {
        return this.json(decline(), {});
    }

    /** «Поповнити онлайн» (2026-10-27): the network's clubs, each with whether it takes it now. */
    topUpOptions(networkId: number): Promise<TopUpOptions> {
        return this.json(topUpOptions({ query: { network_id: networkId } }));
    }

    startTopUp(clubId: number, amount: number): Promise<OnlinePaymentView> {
        return this.json(topUpStart(), { club_id: clubId, amount });
    }

    topUpStatus(id: string): Promise<OnlinePaymentView> {
        return this.json(topUpShow(id));
    }

    uploadAvatar(file: File): Promise<Account> {
        const body = new FormData();
        body.append('avatar', file);

        return this.request<Account>(storeAvatar(), body);
    }

    removeAvatar(): Promise<Account> {
        return this.json(destroyAvatar());
    }

    private json<T>(route: Route, body?: unknown): Promise<T> {
        return this.request<T>(
            route,
            body === undefined ? undefined : JSON.stringify(body),
        );
    }

    private async request<T>(
        route: Route,
        body?: BodyInit,
        retried = false,
    ): Promise<T> {
        const response = await fetch(this.base + route.url, {
            method: route.method.toUpperCase(),
            headers: {
                Accept: 'application/json',
                ...(body instanceof FormData
                    ? {}
                    : { 'Content-Type': 'application/json' }),
                ...(this.sessionToken
                    ? { 'X-Mini-App-Session': this.sessionToken }
                    : {}),
                ...(this.accessToken
                    ? { Authorization: `Bearer ${this.accessToken}` }
                    : {}),
            },
            body: ['GET', 'HEAD'].includes(route.method.toUpperCase())
                ? undefined
                : body,
        });
        const payload = (await response.json().catch(() => ({}))) as Partial<
            Envelope<T>
        > & {
            message?: string;
            code?: string;
            errors?: Record<string, string[]>;
        };

        // The token ran out (401, signed in) or the Mini App session did (422 `session_expired`,
        // 30 minutes): sign in again once, then repeat the request.
        const expired =
            (response.status === 401 && this.accessToken !== null) ||
            (response.status === 422 && payload.code === 'session_expired');

        if (expired && !retried && (await this.renew())) {
            return this.request<T>(route, body, true);
        }

        if (!response.ok) {
            const first = Object.values(payload.errors ?? {})[0]?.[0];

            throw new ApiError(
                first ?? payload.message ?? 'Something went wrong.',
                response.status,
                payload.errors,
                payload.code ?? null,
            );
        }

        return payload.data as T;
    }
}
